/**
 * Script Injector Component
 *
 * Injects scripts into the page head or footer based on CMS settings.
 * Supports both global scripts (from settings) and per-page scripts.
 */

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { loadScriptsSettings } from '@/lib/siteSettings';

interface ScriptInjectorProps {
  headScripts?: string;
  footerScripts?: string;
  useGlobalScripts?: boolean;
}

export const ScriptInjector: React.FC<ScriptInjectorProps> = ({
  headScripts,
  footerScripts,
  useGlobalScripts = true,
}) => {
  const [globalScripts, setGlobalScripts] = useState<{
    head_scripts?: string;
    footer_scripts?: string;
  } | null>(null);

  useEffect(() => {
    if (useGlobalScripts) {
      loadScriptsSettings().then((scripts) => {
        if (scripts) {
          setGlobalScripts(scripts);
        }
      });
    }
  }, [useGlobalScripts]);

  // Combine global and page-specific scripts
  const finalHeadScripts = [useGlobalScripts && globalScripts?.head_scripts, headScripts]
    .filter(Boolean)
    .join('\n');

  const finalFooterScripts = [useGlobalScripts && globalScripts?.footer_scripts, footerScripts]
    .filter(Boolean)
    .join('\n');

  if (!finalHeadScripts && !finalFooterScripts) {
    return null;
  }

  return (
    <Helmet>
      {finalHeadScripts && (
        <script type="text/javascript" dangerouslySetInnerHTML={{ __html: finalHeadScripts }} />
      )}
      {finalFooterScripts && (
        <script type="text/javascript" dangerouslySetInnerHTML={{ __html: finalFooterScripts }} />
      )}
    </Helmet>
  );
};

// Hook for programmatic script injection
export function useScriptInjection(headScripts?: string, footerScripts?: string) {
  useEffect(() => {
    // Inject head scripts
    if (headScripts) {
      const script = document.createElement('script');
      script.innerHTML = headScripts;
      document.head.appendChild(script);

      return () => {
        // Cleanup on unmount
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, [headScripts]);

  useEffect(() => {
    // Inject footer scripts
    if (footerScripts) {
      const script = document.createElement('script');
      script.innerHTML = footerScripts;
      document.body.appendChild(script);

      return () => {
        // Cleanup on unmount
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, [footerScripts]);
}
